import React, { useState } from 'react';
import imageCompression from 'browser-image-compression';
import PhotoCapture from './PhotoCapture';
import PhotoReview from './PhotoReview';
import SuperheroTransform from './SuperheroTransform';
import '../styles/Photo.css';

const Photo: React.FC = () => {
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showSuperheroTransform, setShowSuperheroTransform] = useState(false);
  const [superheroImage, setSuperheroImage] = useState<string | null>(null);
  
  const handlePhotoCapture = (imageData: string) => {
    setCapturedImage(imageData);
    setUploadError(null);
    setUploadSuccess(false);
  };
  
  const handleRetake = () => {
    setCapturedImage(null);
    setUploadError(null);
    setUploadSuccess(false);
  };
  
  const handleApprove = async () => {
    if (!capturedImage) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Manual conversion: Fetch data URL, get blob, create File
      const res = await fetch(capturedImage);
      const blob = await res.blob();
      const imageFile = new File([blob], "selfie.jpg", { type: blob.type || 'image/jpeg' });

      // Define compression options
      const options = {
        maxSizeMB: 0.1, // 100KB
        maxWidthOrHeight: undefined, // Keep original dimensions initially
        useWebWorker: true,
        fileType: 'image/jpeg', // Ensure output is JPG
      };

      console.log(`Original size before compression: ${imageFile.size / 1024} KB`);

      // Compress the image File
      const compressedBlob = await imageCompression(imageFile, options);
      console.log(`Compressed size: ${compressedBlob.size / 1024} KB`);
      
      // Create form data with the compressed blob
      const formData = new FormData();
      // Use the compressedBlob, ensuring the filename is set
      formData.append('file', compressedBlob, 'selfie.jpg'); 
      
      // Upload to R2 via a Workers function
      const uploadResponse = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      
      if (!uploadResponse.ok) {
        // Provide more specific error from response if possible
        const errorText = await uploadResponse.text();
        throw new Error(`Failed to upload image. Status: ${uploadResponse.status}. ${errorText}`);
      }
      
      const result = await uploadResponse.json();
      setUploadSuccess(true);
      console.log('Upload successful:', result);
    } catch (error) {
      console.error('Error processing or uploading image:', error);
      // Provide more specific error message to the user
      if (error instanceof Error) {
           setUploadError(`Error: ${error.message}. Please try again.`);
      } else {
           setUploadError('An unknown error occurred during image processing or upload. Please try again.');
      }
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleSuperheroSuccess = (imageUrl: string) => {
    setSuperheroImage(imageUrl);
  };
  
  const handleSuperheroError = (error: string) => {
    setUploadError(error);
  };
  
  return (
    <div className="photo-section">
      <h1 className="photo-title">Take a Selfie or Upload a Photo</h1>
      <p className="photo-subtitle">Transform your photo into a superhero!</p>
      
      {!capturedImage ? (
        <PhotoCapture onPhotoCapture={handlePhotoCapture} />
      ) : (
        <>
          {!uploadSuccess && !showSuperheroTransform ? (
            <PhotoReview 
              imageData={capturedImage}
              onApprove={handleApprove}
              onRetake={handleRetake}
              isUploading={isUploading}
            />
          ) : null}
          
          {uploadSuccess && !showSuperheroTransform && (
            <div className="success-message">
              <span className="success-icon">✅</span>
              Image uploaded successfully!
              <div className="success-actions">
                <button onClick={handleRetake} className="action-btn">
                  Take Another Photo
                </button>
                <button 
                  onClick={() => setShowSuperheroTransform(true)} 
                  className="action-btn transform-btn"
                >
                  Transform into Superhero
                </button>
              </div>
            </div>
          )}
          
          {showSuperheroTransform && (
            <SuperheroTransform 
              faceImageUrl={capturedImage}
              onSuccess={handleSuperheroSuccess}
              onError={handleSuperheroError}
            />
          )}
        </>
      )}
      
      {uploadError && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {uploadError}
        </div>
      )}
    </div>
  );
};

export default Photo;